import { useState, useEffect } from 'react';
import { SupabaseSong } from '../types/supabase';
import { spotifyService } from '../services/spotify';
import { AlbumArtwork } from './AlbumArtwork';
import spotifyIcon from '../assets/icons/spotify.svg';

interface PlayerProps {
  currentSong: SupabaseSong | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onVolumeChange: (volume: number) => void;
}

export const Player = ({
  currentSong,
  isPlaying,
  onPlayPause,
  onNext,
  onPrevious,
  onVolumeChange,
}: PlayerProps) => {
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying && !isDragging) {
        setProgress(spotifyService.getCurrentProgress());
        setDuration(spotifyService.getCurrentDuration());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, isDragging]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newProgress = Number(e.target.value);
    setProgress(newProgress);
    spotifyService.seekTo(newProgress);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    onVolumeChange(newVolume);
  };

  return (
    <div className="w-full rounded-lg bg-background-light p-4 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:bg-background-dark">
      <div className="mx-auto flex max-w-4xl flex-col space-y-3">
        {/* Song Info */}
        <div className="flex items-center gap-3">
          <AlbumArtwork
            src={currentSong?.album_artwork || null}
            alt={currentSong?.title || ''}
            className="h-14 w-14 rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-medium leading-tight text-gray-900 dark:text-gray-100">
              {currentSong?.title}
            </h3>
            <p className="mt-0.5 truncate text-[13px] leading-tight text-gray-600 dark:text-gray-400">
              {currentSong?.artist}
            </p>
            {currentSong?.spotify_id && (
              <div className="mt-0.5 h-[15px]">
                <img
                  src={spotifyIcon}
                  alt="Spotify"
                  className="h-full w-auto text-gray-500 dark:text-gray-400"
                />
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex w-full items-center gap-2">
          <span className="w-12 text-right text-[13px] text-gray-600 dark:text-gray-400">
            {formatTime(progress)}
          </span>
          <div className="flex-1">
            <input
              type="range"
              min="0"
              max={duration}
              value={progress}
              onChange={handleProgressChange}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              className="h-1 w-full cursor-pointer appearance-none rounded-full bg-gray-200 dark:bg-gray-700 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
              style={{
                background: `linear-gradient(to right, #10B981 ${(progress / duration) * 100}%, #E5E7EB ${(progress / duration) * 100}%)`,
              }}
            />
          </div>
          <span className="w-12 text-[13px] text-gray-600 dark:text-gray-400">
            {formatTime(duration)}
          </span>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onPrevious}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-gray-900 hover:text-primary dark:text-gray-100 dark:hover:text-primary"
              aria-label="Previous track"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={onPlayPause}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-gray-900 hover:text-primary dark:text-gray-100 dark:hover:text-primary"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg
                  className="h-[30px] w-[30px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-[30px] w-[30px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </button>
            <button
              onClick={onNext}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-gray-900 hover:text-primary dark:text-gray-100 dark:hover:text-primary"
              aria-label="Next track"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Volume Control */}
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-gray-600 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={handleVolumeChange}
              className="h-1 w-20 cursor-pointer appearance-none rounded-full bg-gray-200 dark:bg-gray-700 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
              style={{
                background: `linear-gradient(to right, #10B981 ${volume}%, #E5E7EB ${volume}%)`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
